// biome-ignore-all lint/a11y/useSemanticElements: role="button" is intentional; the element wraps nested interactive children, so it cannot be a native button (Task #25).
// =============================================================================
// ResponderChildView.jsx
// =============================================================================
// The staff/responder per-child editor — for the selected child + selected
// category (fees / uniforms / shoes / device) it renders the full editable
// detail panel inline plus the per-item approve/decline controls. Lets
// staff fix a typo, add a brochure, change an item, or set its approval
// status without leaving the approvals page.
//
// Reuses the ChildXxxSection components from form/ChildSections.jsx (same
// editors the shelter wizard uses). Approval state and save handlers are
// owned here; ApprovalDecisions (sibling panel) renders the summary +
// confirm-and-send flow over the same approval state on req.children.
//
// Pulled out of RequestDetail.jsx as part of the Phase C extraction pass.
// ~1450 lines, the largest single React component in the codebase. Most
// of the heft is render JSX for the four category panels plus the inline
// editor that mirrors the wizard's per-child sections.
//
// Props:
//   req                       - the request being approved
//   user                      - logged-in staff user (audit + role checks)
//   onSaveNoDb                - update local state only (no DB write)
//   showSaved                 - toast helper
//   setStatus                 - optional setter for the request's overall status
//   slSubmitState             - School Locker submit state (parent-owned)
//   slSubmitMsg               - School Locker submit message
// =============================================================================
import React from "react";
import { canAccess, isSuperUser } from "../../app/access";
import { useSession } from "../../app/SessionContext.jsx";
import { useCommentTemplates } from "../../hooks/useCommentTemplates";
import { useShelterApprovalHold } from "../../hooks/useShelterApprovalHold";
import { useStandardCosts } from "../../hooks/useStandardCosts";
import { supplierMeta, useUniformSuppliers } from "../../hooks/useUniformSuppliers";
import { collectSessionDates } from "../../services/actionSheets";
import { isApprovalActionBlocked } from "../../services/approvalHold";
import {
  applyItemDecision,
  isLockedFullyProcessed,
  SECTION_STATUS_BG,
} from "../../services/approvals";
import {
  deliveryApprovalBlockReason,
  isStationeryPackFeeType,
} from "../../services/deliveryApprovalHold";
import { alertDialog } from "../../services/dialogService";
import { PANEL } from "../../utils/constants";
import { APPROVAL_STATUS_VALUES, APPROVAL_STATUSES } from "../../utils/enums";
import { calcUniItems } from "../../utils/formConstants";
import { GLYPH } from "../../utils/glyphs";
import { money } from "../../utils/helpers";
import { openSchoolLockerOrderPdf } from "../../utils/schoolLockerOrderPdf";
import { openUniformAuthorityPdf } from "../../utils/uniformAuthorityPdf";
import {
  ChildFeesSection,
  ChildLaptopSection,
  ChildShoesSection,
  ChildUniformsSection,
  printChild,
} from "../form/ChildSections.jsx";
import { HelpButton } from "../form/HelpButton.jsx";
import { ActionSheetButtons } from "./ActionSheetButtons.jsx";
import { ActionSheetModal } from "./ActionSheetModal.jsx";
import { ChildItemReadView } from "./ChildItemReadView.jsx";
import { DeclineReasonModal } from "./DeclineReasonModal.jsx";
import { APPROVAL_COLOURS } from "./RequestDetailAtoms.jsx";

// A small "reason captured" bubble shown to the left of an approval selector while a
// Declined / Needs-Info reason is held but not yet sent. Click to edit. It disappears
// at Confirm & Send (the reason is then in the comment the shelter receives).
function ReasonBubble({ reason, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={`Reason (click to edit): ${reason}`}
      style={{
        border: "1px solid #F9A825",
        background: "#FFF8E1",
        color: "#7A5A00",
        borderRadius: 12,
        padding: "3px 9px",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        marginRight: 8,
        fontFamily: "inherit",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true">{"\u{1F4AD}"}</span> Reason
    </button>
  );
}

export function ResponderChildView({
  req,
  onSaveNoDb,
  showSaved,
  setStatus,
  deriveStatus,
  slSubmitState,
  slSubmitMsg,
  actionSheetOpen,
  setActionSheetOpen,
  nextStepsTemplates,
  // Possible-repeat flag (db-210): ItemRepeat map by fee/device id, the latest Repeat
  // check note, and the look-back months — loaded once in RequestDetail.
  repeats = null,
  repeatReview = null,
  repeatMonths = 12,
}) {
  const { user } = useSession();
  const children = req.children || [];
  // Confirmed-session dates for the Action Sheets buttons on each child card.
  const actionSheetDates = collectSessionDates(children);
  // "Monitored shelter" approval hold: when the shelter is flagged no_approvals,
  // anyone WITHOUT the monitored_approval capability may only set Needs Info —
  // Approve/Decline is blocked with the admin-editable message. See
  // src/services/approvalHold.ts + docs/permissions-and-approval-hold-plan.md.
  const approvalHold = useShelterApprovalHold(req.shelterCode, req.shelterName);
  // Who may approve a monitored service: a SuperUser (always) or a holder of the
  // monitored_approval capability. Computed once; passed to isApprovalActionBlocked.
  const canApproveMonitored = isSuperUser(user) || canAccess(user, "monitored_approval");
  // Admin-driven supplier metadata (db-46) — drives which print buttons appear.
  const { suppliers: uniformSuppliers } = useUniformSuppliers();
  // Admin-configured standard costs — feeds the QLD Stationery-pack amount so a
  // responder's (read-only, checkbox) view uses the same System Content cost the
  // shelter does, not the built-in default.
  const { costs: stdCosts } = useStandardCosts();

  // Selected child index and category key
  const selState = React.useState(null); // null = nothing selected
  const sel = selState[0];
  const setSel = selState[1];
  // sel = { ci: number, cat: "fees"|"uniforms"|"shoes"|"laptop" } or null

  // Edit mode per selection
  const editState = React.useState(false);
  const editing = editState[0];
  const setEditing = editState[1];

  // Saving state
  const savingState = React.useState(false);
  const saving = savingState[0];
  const setSaving = savingState[1];

  // Load templates for post-approval comment suggestions
  const _tdbInner = useCommentTemplates();
  const _allTemplates = _tdbInner.templates;

  // Local approval state — mirrors ApprovalDecisions pattern
  function initApprovals() {
    const state = {};
    children.forEach(function (child, ci) {
      state[ci] = {
        fees: (child.fees || []).map(function (f) {
          return f.approvalStatus || APPROVAL_STATUSES.PENDING;
        }),
        uniforms: child.uniforms
          ? child.uniforms.approvalStatus || APPROVAL_STATUSES.PENDING
          : null,
        shoes: child.shoes ? child.shoes.approvalStatus || APPROVAL_STATUSES.PENDING : null,
        laptop: child.laptop ? child.laptop.approvalStatus || APPROVAL_STATUSES.PENDING : null,
      };
    });
    return state;
  }
  const appState = React.useState(initApprovals);
  const approvals = appState[0];
  const setApprovals = appState[1];
  const sessionStartApprovalsRef = React.useRef(null); // Only set on req.id change — never overwritten by status changes
  const latestChildrenRef = React.useRef(null);
  // Snapshot of the children taken when the detail editor is OPENED, so Cancel /
  // Close Edit can revert the in-progress edits. The editor commits live (each
  // onChange -> onSaveNoDb updates the in-memory req so totals preview live), so
  // without this snapshot there is nothing to roll back to. Save keeps the edits
  // (clears the snapshot); Cancel / Close restores it. Approval statuses are
  // preserved on restore so reverting a detail edit never undoes a decision.
  const editSnapshotRef = React.useRef(null);
  // Always-fresh refs used by the osd-watch realtime handler below. The handler
  // is set up once per req.id, but real-time events can arrive after req has
  // been updated (e.g. notes saved) — without these refs the merge would use
  // a stale req and clobber newer state.
  const reqRef = React.useRef(req);
  reqRef.current = req;
  const onSaveNoDbRef = React.useRef(onSaveNoDb);
  onSaveNoDbRef.current = onSaveNoDb;
  // Re-sync approvals when req changes (id change = new request, children change = save/confirm)
  const childrenKey = JSON.stringify(
    (req.children || []).map(function (c) {
      return {
        fees: (c.fees || []).map(function (f) {
          return f.approvalStatus || APPROVAL_STATUSES.PENDING;
        }),
        uni: c.uniforms ? c.uniforms.approvalStatus || APPROVAL_STATUSES.PENDING : null,
        shoes: c.shoes ? c.shoes.approvalStatus || APPROVAL_STATUSES.PENDING : null,
        laptop: c.laptop ? c.laptop.approvalStatus || APPROVAL_STATUSES.PENDING : null,
      };
    })
  );
  React.useEffect(
    function () {
      latestChildrenRef.current = req.children || [];
      setApprovals(initApprovals());
    },
    [req.id, childrenKey]
  );
  // db-79: remember each item's originally-requested amount at load — the shared edit
  // form rebuilds items and drops extra fields, so a responder amount-edit would lose it.
  // Captured on req.id only (amount edits don't change req.id), so it stays the original.
  const requestedByChildRef = React.useRef({});
  React.useEffect(
    function () {
      const map = {};
      (req.children || []).forEach(function (c, ci) {
        const fees = {};
        (c.fees || []).forEach(function (f) {
          if (f && f.feeType != null) fees[f.feeType] = f.requestedAmount;
        });
        map[ci] = {
          fees: fees,
          uniforms: c.uniforms ? c.uniforms.requestedAmount : undefined,
          shoes: c.shoes ? c.shoes.requestedAmount : undefined,
          laptop: c.laptop ? c.laptop.requestedAmount : undefined,
        };
      });
      requestedByChildRef.current = map;
    },
    [req.id]
  );
  React.useEffect(
    function () {
      // Auto-select the first child + first available category on load so the
      // responder lands directly in the editing experience instead of having
      // to figure out the click-the-pill model. They can still click any card.
      const CATS = [
        {
          key: "fees",
          has: function (c) {
            return c.fees && c.fees.length > 0;
          },
        },
        {
          key: "uniforms",
          has: function (c) {
            return !!c.uniforms;
          },
        },
        {
          key: "shoes",
          has: function (c) {
            return !!c.shoes;
          },
        },
        {
          key: "laptop",
          has: function (c) {
            return !!c.laptop;
          },
        },
      ];
      let found = null;
      (req.children || []).some(function (child, ci) {
        const hit = CATS.find(function (b) {
          return b.has(child);
        });
        if (hit) {
          found = { ci: ci, cat: hit.key };
          return true;
        }
        return false;
      });
      setSel(found);
      setEditing(false);
      // Capture statuses at session start — this ref is NEVER overwritten by status changes
      sessionStartApprovalsRef.current = initApprovals();
    },
    [req.id]
  );
  React.useEffect(
    function () {
      // Keep latestChildrenRef in sync when req prop updates (after each onSaveNoDb)
      if (req.children && req.children.length > 0) {
        latestChildrenRef.current = req.children;
      }
    },
    [req.children]
  );

  // ── Detail-editor open/cancel/commit (snapshot-based revert) ───────────────
  // deep-copy via JSON: children are plain data (qty/cost/items/approvalStatus).
  function snapshotChildren() {
    return JSON.parse(JSON.stringify(req.children || []));
  }
  function beginEdit() {
    editSnapshotRef.current = snapshotChildren();
    setEditing(true);
  }
  // Restore the snapshot's detail fields but KEEP each item's CURRENT approval
  // status, so cancelling a price/qty edit never reverts an approve/decline made
  // (via the status dropdown) while the editor happened to be open.
  function cancelEdit() {
    const snap = editSnapshotRef.current;
    if (snap) {
      const cur = latestChildrenRef.current || req.children || [];
      const restored = snap.map(function (sc, i) {
        const cc = cur[i];
        if (!cc) return sc;
        const out = Object.assign({}, sc);
        if (Array.isArray(sc.fees)) {
          out.fees = sc.fees.map(function (sf, j) {
            const cf = cc.fees?.[j];
            return cf ? Object.assign({}, sf, { approvalStatus: cf.approvalStatus }) : sf;
          });
        }
        ["uniforms", "shoes", "laptop"].forEach(function (k) {
          if (sc[k] && cc[k]) {
            out[k] = Object.assign({}, sc[k], { approvalStatus: cc[k].approvalStatus });
          }
        });
        return out;
      });
      latestChildrenRef.current = restored;
      onSaveNoDb(Object.assign({}, req, { children: restored }));
    }
    editSnapshotRef.current = null;
    setEditing(false);
  }
  function getCatStatus(ci, cat) {
    const a = approvals[ci];
    if (!a) return null;
    let catSts = [];
    if (cat === "fees") {
      catSts = a.fees || [];
      if (!catSts.length) return null;
    } else {
      const v = a[cat];
      if (!v) return null;
      catSts = [v];
    }
    const allApp = catSts.every(function (s) {
      return s === APPROVAL_STATUSES.APPROVED;
    });
    const allDec = catSts.every(function (s) {
      return s === APPROVAL_STATUSES.DECLINED;
    });
    const anyPen = catSts.some(function (s) {
      return s !== APPROVAL_STATUSES.APPROVED && s !== "Declined";
    });
    if (allApp) return APPROVAL_STATUSES.APPROVED;
    if (allDec) return APPROVAL_STATUSES.DECLINED;
    if (anyPen) return APPROVAL_STATUSES.PENDING;
    return "Mixed";
  }

  // Per-child totals by approval status
  function childTotals(ci) {
    const child = children[ci];
    if (!child) return { unapproved: 0, declined: 0, approved: 0 };
    const a = approvals[ci] || {};
    let unapproved = 0,
      declined = 0,
      approved = 0;

    // Fees
    (child.fees || []).forEach(function (f, fi) {
      const amt = parseFloat(f.feeAmount) || 0;
      const st = a.fees?.[fi] || APPROVAL_STATUSES.PENDING;
      if (st === APPROVAL_STATUSES.APPROVED) approved += amt;
      else if (st === APPROVAL_STATUSES.DECLINED) declined += amt;
      else unapproved += amt;
    });

    // Uniforms
    if (child.uniforms) {
      const uAmt = calcUniItems([child.uniforms]);
      const uSt = a.uniforms || APPROVAL_STATUSES.PENDING;
      if (uSt === APPROVAL_STATUSES.APPROVED) approved += uAmt;
      else if (uSt === APPROVAL_STATUSES.DECLINED) declined += uAmt;
      else unapproved += uAmt;
    }

    // Shoes
    if (child.shoes) {
      const sAmt =
        (child.shoes.jQty || 0) * (parseFloat(child.shoes.jCost) || 0) +
        (child.shoes.lQty || 0) * (parseFloat(child.shoes.lCost) || 0);
      const sSt = a.shoes || APPROVAL_STATUSES.PENDING;
      if (sSt === APPROVAL_STATUSES.APPROVED) approved += sAmt;
      else if (sSt === APPROVAL_STATUSES.DECLINED) declined += sAmt;
      else unapproved += sAmt;
    }

    // Device
    if (child.laptop) {
      const lAmt = parseFloat(child.laptop.estimatedCost) || 0;
      const lSt = a.laptop || APPROVAL_STATUSES.PENDING;
      if (lSt === APPROVAL_STATUSES.APPROVED) approved += lAmt;
      else if (lSt === APPROVAL_STATUSES.DECLINED) declined += lAmt;
      else unapproved += lAmt;
    }

    return { unapproved: unapproved, declined: declined, approved: approved };
  }

  function statusColour(st) {
    // Colours come from the shared SECTION_STATUS_BG (services/approvals) so the
    // responder and shelter section buttons stay in lock-step. Unknown/Pending = gold.
    return {
      bg: SECTION_STATUS_BG[st] || SECTION_STATUS_BG[APPROVAL_STATUSES.PENDING],
      text: "#fff",
    };
  }

  // ── Decline / Needs-Info reason capture ─────────────────────────────────────
  // A REQUIRED reason modal opens when an item is Declined or set to Needs Info. The
  // reason is held on the in-memory item and folded into the shelter comment at
  // Confirm & Send. reasonModal = { status, itemLabel, initial, onAdd, onCancel } | null.
  const [reasonModal, setReasonModal] = React.useState(null);

  function needsReason(status) {
    return status === APPROVAL_STATUSES.DECLINED || status === APPROVAL_STATUSES.NEEDS_INFO;
  }

  // The reason currently held for a category's items ("" if none) — powers the
  // reason bubble next to the category selector.
  function categoryReason(ci, cat) {
    const child = children?.[ci];
    if (!child) return "";
    if (cat === "fees") {
      const f = (child.fees || []).find(function (x) {
        return x?.reason;
      });
      return f ? String(f.reason) : "";
    }
    return child[cat]?.reason ? String(child[cat].reason) : "";
  }

  // The Reason type (db-207) held alongside that reason — pre-fills the modal on edit.
  function categoryReasonType(ci, cat) {
    const child = children?.[ci];
    if (!child) return "";
    if (cat === "fees") {
      const f = (child.fees || []).find(function (x) {
        return x?.reasonCategory;
      });
      return f ? String(f.reasonCategory) : "";
    }
    return child[cat]?.reasonCategory ? String(child[cat].reasonCategory) : "";
  }

  function decisionLabel(ci, cat) {
    const catName =
      cat === "fees"
        ? "School Costs & Stationery"
        : cat === "laptop"
          ? "Device"
          : cat === "uniforms"
            ? "Uniforms"
            : cat === "shoes"
              ? "Shoes"
              : cat;
    return `Child ${ci + 1} — ${catName}`;
  }

  // Open the modal for a whole-category decision (saveApproval path). commit* are
  // hoisted function declarations, so referencing them here is fine.
  function openReasonModal(newStatus, ci, cat, existingReason) {
    setReasonModal({
      status: newStatus,
      itemLabel: decisionLabel(ci, cat),
      initial: existingReason || "",
      initialCategory: existingReason ? categoryReasonType(ci, cat) : "",
      onAdd: function (reason, reasonType) {
        setReasonModal(null);
        commitApproval(ci, cat, newStatus, reason, reasonType);
      },
      onCancel: function () {
        setReasonModal(null);
      },
    });
  }

  // Open the modal for a single-fee decision (saveFeeApproval path).
  function openFeeReasonModal(ci, fi, newStatus, existingReason) {
    setReasonModal({
      status: newStatus,
      itemLabel: `Child ${ci + 1} — ${children?.[ci]?.fees?.[fi]?.feeType || "fee item"}`,
      initial: existingReason || "",
      initialCategory: existingReason
        ? String(children?.[ci]?.fees?.[fi]?.reasonCategory || "")
        : "",
      onAdd: function (reason, reasonType) {
        setReasonModal(null);
        commitFeeApproval(ci, fi, newStatus, reason, reasonType);
      },
      onCancel: function () {
        setReasonModal(null);
      },
    });
  }

  // Save approval for currently selected child+category
  async function saveApproval(newStatus) {
    if (!sel) return;
    // Monitored shelter: a responder can't Approve/Decline — show the message and
    // cancel the change (the controlled select reverts to its previous value).
    if (isApprovalActionBlocked(canApproveMonitored, approvalHold.noApprovals, newStatus)) {
      await alertDialog({ message: approvalHold.message });
      return;
    }
    // Verified-address block (Batch 5b): can't approve a DISPATCHED item — a Device
    // (laptop category) or a Stationery Pack (a Stationery fee) — while the request's
    // delivery address is pending Zephyr verification.
    const selChild = req.children?.[sel.ci];
    const selIsDispatch =
      sel.cat === "laptop" ||
      (sel.cat === "fees" &&
        (selChild?.fees || []).some((f) => isStationeryPackFeeType(f?.feeType)));
    const selBlockReason = deliveryApprovalBlockReason({
      deliveryAddressVerified: req.deliveryAddressVerified,
      deliveryAddressBanned: req.deliveryAddressBanned,
      isDispatchItem: selIsDispatch,
      status: newStatus,
    });
    if (selBlockReason) {
      await alertDialog({ message: selBlockReason });
      return;
    }
    if (needsReason(newStatus)) {
      openReasonModal(newStatus, sel.ci, sel.cat, null);
      return;
    }
    commitApproval(sel.ci, sel.cat, newStatus, null);
  }

  // The apply step for a whole-category decision — parametrised so the reason modal
  // can invoke it on Add. Attaches the responder's reason to the affected item(s) in
  // memory (folded into the shelter comment at Confirm & Send).
  function commitApproval(ci, cat, newStatus, reasonText, reasonType) {
    setSaving(true);

    // Update local approvals
    const newApprovals = Object.assign({}, approvals);
    const childApp = Object.assign({}, newApprovals[ci]);
    if (cat === "fees") {
      childApp.fees = childApp.fees.map(function () {
        return newStatus;
      });
    } else {
      childApp[cat] = newStatus;
    }
    newApprovals[ci] = childApp;
    setApprovals(newApprovals);

    // Build updatedReq — use latestChildrenRef as the base so we always have the most
    // recently saved child data (req prop may lag behind due to React batching)
    const baseChildren =
      latestChildrenRef.current && latestChildrenRef.current.length > 0
        ? latestChildrenRef.current
        : req.children || [];
    const updatedReq = Object.assign({}, req);
    updatedReq.children = baseChildren.map(function (child, idx) {
      const cApp = newApprovals[idx] || {};
      const updated = Object.assign({}, child);
      updated.fees = (child.fees || []).map(function (f, fi) {
        return applyItemDecision(f, cApp.fees?.[fi]);
      });
      if (child.uniforms) updated.uniforms = applyItemDecision(child.uniforms, cApp.uniforms);
      if (child.shoes) updated.shoes = applyItemDecision(child.shoes, cApp.shoes);
      if (child.laptop) updated.laptop = applyItemDecision(child.laptop, cApp.laptop);
      return updated;
    });
    // Attach the responder's reason to the item(s) this category decision covers.
    if (needsReason(newStatus)) {
      updatedReq.children = updatedReq.children.map(function (child, idx) {
        if (idx !== ci) return child;
        if (cat === "fees") {
          return Object.assign({}, child, {
            fees: (child.fees || []).map(function (f) {
              return Object.assign({}, f, {
                reason: reasonText,
                reasonCategory: reasonType || undefined,
              });
            }),
          });
        }
        if (child[cat]) {
          return Object.assign({}, child, {
            [cat]: Object.assign({}, child[cat], {
              reason: reasonText,
              reasonCategory: reasonType || undefined,
            }),
          });
        }
        return child;
      });
    }
    latestChildrenRef.current = updatedReq.children;

    const newOverallStatus = deriveStatus(updatedReq.children);
    setSaving(false);

    // Ephemeral staging: per-item approve/decline does NOT write to the DB here.
    // The in-memory request is updated via onSaveNoDb below so both approval
    // panels stay in sync; the decisions are persisted only at "Confirm & Send"
    // (which also creates picker tasks + auto-completes tasks). A crash or refresh
    // before confirm discards them and the request stays "Submitted".
    // See docs/ephemeral-staging-plan.md.

    const savedReq = Object.assign({}, updatedReq, {
      status: newOverallStatus || req.status,
      updatedBy: user.shelterName,
      updatedAt: new Date().toISOString(),
      approvalsConfirmedAt: null,
    });
    onSaveNoDb(savedReq);
    if (setStatus && savedReq.status) setStatus(savedReq.status);
    showSaved(`Saved ${GLYPH.check}`);
    // Templates are now posted by confirmAndSend() — not on individual approval
    // saves. The School Locker order is likewise submitted at Confirm & Send
    // (ApprovalDecisions.confirmAndSend), NOT here — so nothing irreversible fires
    // before the responder confirms.
  }

  // Save approval for a single fee item by index
  async function saveFeeApproval(ci, fi, newStatus) {
    // Monitored shelter: block a responder's Approve/Decline (revert the select).
    if (isApprovalActionBlocked(canApproveMonitored, approvalHold.noApprovals, newStatus)) {
      await alertDialog({ message: approvalHold.message });
      return;
    }
    // Delivery block (Batch 5b + db-147): a Stationery-Pack fee can't be approved
    // while the delivery address is pending verification OR banned.
    const dispatchFee = req.children?.[ci]?.fees?.[fi];
    const feeBlockReason = deliveryApprovalBlockReason({
      deliveryAddressVerified: req.deliveryAddressVerified,
      deliveryAddressBanned: req.deliveryAddressBanned,
      isDispatchItem: isStationeryPackFeeType(dispatchFee?.feeType),
      status: newStatus,
    });
    if (feeBlockReason) {
      await alertDialog({ message: feeBlockReason });
      return;
    }
    if (needsReason(newStatus)) {
      openFeeReasonModal(ci, fi, newStatus, null);
      return;
    }
    commitFeeApproval(ci, fi, newStatus, null);
  }

  // The apply step for a single-fee decision — parametrised so the reason modal can
  // invoke it on Add. Attaches the reason to just that fee (in-memory).
  function commitFeeApproval(ci, fi, newStatus, reasonText, reasonType) {
    setSaving(true);
    const newApprovals = Object.assign({}, approvals);
    const childApp = Object.assign({}, newApprovals[ci]);
    const fees = childApp.fees.slice();
    fees[fi] = newStatus;
    childApp.fees = fees;
    newApprovals[ci] = childApp;
    setApprovals(newApprovals);

    const baseChildren =
      latestChildrenRef.current && latestChildrenRef.current.length > 0
        ? latestChildrenRef.current
        : req.children || [];
    const updatedReq = Object.assign({}, req);
    updatedReq.children = baseChildren.map(function (child, idx) {
      const cApp = newApprovals[idx] || {};
      const updated = Object.assign({}, child);
      updated.fees = (child.fees || []).map(function (f, fIdx) {
        return applyItemDecision(f, cApp.fees?.[fIdx]);
      });
      if (child.uniforms) updated.uniforms = applyItemDecision(child.uniforms, cApp.uniforms);
      if (child.shoes) updated.shoes = applyItemDecision(child.shoes, cApp.shoes);
      if (child.laptop) updated.laptop = applyItemDecision(child.laptop, cApp.laptop);
      return updated;
    });
    // Attach the responder's reason to just this fee (in-memory only).
    if (needsReason(newStatus)) {
      updatedReq.children = updatedReq.children.map(function (child, idx) {
        if (idx !== ci) return child;
        return Object.assign({}, child, {
          fees: (child.fees || []).map(function (f, j) {
            return j === fi
              ? Object.assign({}, f, {
                  reason: reasonText,
                  reasonCategory: reasonType || undefined,
                })
              : f;
          }),
        });
      });
    }
    latestChildrenRef.current = updatedReq.children;

    const newOverallStatus = deriveStatus(updatedReq.children);
    setSaving(false);

    // Ephemeral staging: no DB write here — the decision is held in the in-memory
    // request (via onSaveNoDb below) and persisted only at "Confirm & Send".
    // See docs/ephemeral-staging-plan.md.

    const savedReq = Object.assign({}, updatedReq, {
      status: newOverallStatus || req.status,
      updatedBy: user.shelterName,
      updatedAt: new Date().toISOString(),
      approvalsConfirmedAt: null,
    });
    onSaveNoDb(savedReq);
    if (setStatus && savedReq.status) setStatus(savedReq.status);
    showSaved("Saved \u2713");
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  // Determine which child and category are selected
  const selChild = sel ? children[sel.ci] : null;
  const selCat = sel ? sel.cat : null;

  // D-2/D-4 terminal lock (Slice 2): a CONFIRMED Fully Processed request is
  // finalised — no reopen/reverse. Every item is already decided + locked by the
  // per-item locks below (they key on approvalsConfirmedAt + a decided item), so
  // this is behaviour-identical today; it makes the request-level terminal lock
  // explicit and keeps it enforced even if the granular per-item lock is ever
  // relaxed. Keyed on confirmation, so pre-confirm staging is never blocked.
  const requestLocked = isLockedFullyProcessed(req);

  // Category button config
  const CAT_BTNS = [
    { key: "fees", label: "School Costs & Stationery" },
    { key: "uniforms", label: "Uniforms" },
    { key: "shoes", label: "Shoes" },
    { key: "laptop", label: "Device" },
  ];

  return (
    <div>
      {reasonModal && (
        <DeclineReasonModal
          status={reasonModal.status}
          itemLabel={reasonModal.itemLabel}
          initial={reasonModal.initial}
          initialCategory={reasonModal.initialCategory}
          onAdd={reasonModal.onAdd}
          onCancel={reasonModal.onCancel}
        />
      )}
      {/* Action Sheet modal — shared with the shelter view. */}
      <ActionSheetModal
        open={actionSheetOpen}
        onClose={function () {
          setActionSheetOpen(null);
        }}
        req={req}
        nextStepsTemplates={nextStepsTemplates}
      />
      {/* ── How-to banner — only when nothing is selected yet ──────────────── */}
      {!sel && children.length > 0 && (
        <div
          style={{
            background: "#FFF8E1",
            border: "1px solid #FFB300",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 13,
            color: "#7c4a00",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 16 }}>{GLYPH.pointRight}</span>
          <span>Click a category badge on a Child card below to review and approve.</span>
        </div>
      )}

      {/* ── Child tab bar ────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: 12,
          overflowX: "auto",
          paddingBottom: 8,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        {children.map(function (child, ci) {
          const totals = childTotals(ci);
          const isSelected = sel && sel.ci === ci;
          const selectFirstCat = function () {
            const firstCat = CAT_BTNS.find(function (btn) {
              return btn.key === "fees"
                ? child.fees && child.fees.length > 0
                : btn.key === "uniforms"
                  ? !!child.uniforms
                  : btn.key === "shoes"
                    ? !!child.shoes
                    : !!child.laptop;
            });
            if (firstCat && !(sel && sel.ci === ci)) {
              setSel({ ci: ci, cat: firstCat.key });
              setEditing(false);
            }
          };
          return (
            <div
              key={child._key || ci}
              role="button"
              tabIndex={0}
              onClick={selectFirstCat}
              onKeyDown={function (e) {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  selectFirstCat();
                }
              }}
              style={{
                background: isSelected ? "#E0F2F1" : "#FFFFFF",
                border: `2px solid ${isSelected ? "#00695C" : "#CFE0DE"}`,
                borderRadius: 12,
                padding: "12px 14px",
                minWidth: 180,
                maxWidth: 220,
                flexShrink: 0,
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 15, color: "#00695C", marginBottom: 2 }}>
                {`Child ${ci + 1}`}
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: "#607D79",
                  marginBottom: 10,
                  lineHeight: 1.4,
                  minHeight: 40,
                }}
              >
                {(child.gender || "") +
                  (child.yearLevel ? ` · ${child.yearLevel}` : "") +
                  (child.schoolName ? ` · ${child.schoolName}` : "")}
              </div>

              {/* Category buttons — fixed height zone so totals align across cards */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 5,
                  marginBottom: 10,
                  minHeight: 52,
                }}
              >
                {CAT_BTNS.map(function (btn) {
                  const hasData =
                    btn.key === "fees"
                      ? child.fees && child.fees.length > 0
                      : btn.key === "uniforms"
                        ? !!child.uniforms
                        : btn.key === "shoes"
                          ? !!child.shoes
                          : !!child.laptop;
                  if (!hasData) return null;
                  const st = getCatStatus(ci, btn.key);
                  const col = statusColour(st);
                  const isSelBtn = sel && sel.ci === ci && sel.cat === btn.key;
                  return (
                    <button
                      key={btn.key}
                      onClick={function (e) {
                        e.stopPropagation();
                        setSel({ ci: ci, cat: btn.key });
                        setEditing(false);
                      }}
                      style={{
                        background: col.bg,
                        color: col.text,
                        border: isSelBtn ? "2px solid #1A2E2B" : "2px solid transparent",
                        borderRadius: 6,
                        padding: "4px 9px",
                        fontSize: 11,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        outline: "none",
                        alignSelf: "flex-start",
                      }}
                    >
                      {btn.label}
                    </button>
                  );
                })}
              </div>

              {/* Totals — always at the bottom, aligned across cards */}
              <div
                style={{
                  fontSize: 14,
                  lineHeight: 1.9,
                  borderTop: `1px solid ${"#CFE0DE"}`,
                  paddingTop: 8,
                  marginTop: "auto",
                }}
              >
                <div style={{ color: "#F9A825", fontWeight: 600 }}>
                  {`Unapproved  ${money(totals.unapproved)}`}
                </div>
                <div style={{ color: "#C62828", fontWeight: 600 }}>
                  {`Declined      ${money(totals.declined)}`}
                </div>
                <div style={{ color: "#388E3C", fontWeight: 600 }}>
                  {`Approved     ${money(totals.approved)}`}
                </div>
              </div>
              <ActionSheetButtons
                child={child}
                ci={ci}
                sessionDates={actionSheetDates}
                reqState={req.state || ""}
                uniformSuppliers={uniformSuppliers}
                onOpen={setActionSheetOpen}
              />
            </div>
          );
        })}
      </div>

      {/* ── Detail panel ────────────────────────────────────────────────── */}
      {sel && selChild && (
        <div
          style={{
            background: "#FFFFFF",
            border: `1px solid ${"#CFE0DE"}`,
            borderRadius: 12,
            padding: "16px 20px",
            marginBottom: 16,
          }}
        >
          {/* Panel header: Child label + Edit (left) | Approve/Decline (right) */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                background: "#00695C",
                color: "white",
                borderRadius: 8,
                padding: "6px 16px",
                fontWeight: 700,
                fontSize: 16,
              }}
            >
              {`Child ${sel.ci + 1}`}
            </div>
            {(function () {
              // Edit button colour follows the category's approval state so it
              // matches the green/red/gold badge shown on the Child summary card.
              // Once approvals have been confirmed (Save & Send to Shelter), the
              // button is disabled so prior decisions can't be silently amended.
              let headerCatSt;
              if (selCat === "fees") {
                const feeStatuses = (selChild.fees || []).map(function (f) {
                  return f.approvalStatus || APPROVAL_STATUSES.PENDING;
                });
                if (feeStatuses.length === 0) headerCatSt = APPROVAL_STATUSES.PENDING;
                else if (
                  feeStatuses.every(function (s) {
                    return s === APPROVAL_STATUSES.APPROVED;
                  })
                )
                  headerCatSt = APPROVAL_STATUSES.APPROVED;
                else if (
                  feeStatuses.every(function (s) {
                    return s === APPROVAL_STATUSES.DECLINED;
                  })
                )
                  headerCatSt = APPROVAL_STATUSES.DECLINED;
                else headerCatSt = APPROVAL_STATUSES.PENDING;
              } else {
                headerCatSt = selChild[selCat]?.approvalStatus || APPROVAL_STATUSES.PENDING;
              }
              // Match the Child-card section label EXACTLY by reusing the same
              // getCatStatus + statusColour the cards use: green (all approved) /
              // red (all declined) / gold (any pending/needs-info) / green->red
              // gradient (a mix of approved + declined, e.g. School Costs & Stationery
              // with one fee approved and one declined). When editing, stay red.
              const btnBg = editing ? "#C62828" : statusColour(getCatStatus(sel.ci, selCat)).bg;
              // Lock the Edit button only AFTER approvals have been confirmed
              // (Save & Send to Shelter), and only for categories whose decision
              // was finalised. Pending / Needs Info categories stay editable
              // even post-confirmation, so further work can continue. Pre-
              // confirmation, everything stays editable.
              const decisionMade =
                headerCatSt === APPROVAL_STATUSES.APPROVED ||
                headerCatSt === APPROVAL_STATUSES.DECLINED;
              const locked =
                ((!!req.approvalsConfirmedAt && decisionMade) || requestLocked) && !editing;
              const lockMsg =
                "This " +
                (selCat === "fees" ? "category" : selCat) +
                " has been " +
                headerCatSt.toLowerCase() +
                " and the decision has been sent to the DFV Service. " +
                "Change the status back to Pending in the Approval Decisions panel to edit.";
              return (
                <button
                  onClick={function () {
                    if (locked) return;
                    // Save keeps; Cancel / "✕ Close Edit" discard. The header
                    // toggle closes WITHOUT saving, so it reverts like Cancel.
                    if (editing) cancelEdit();
                    else beginEdit();
                  }}
                  disabled={locked}
                  title={locked ? lockMsg : ""}
                  style={{
                    background: btnBg,
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    padding: "7px 18px",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: locked ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                    opacity: locked ? 0.6 : 1,
                  }}
                >
                  {editing
                    ? `${GLYPH.cross} Close Edit`
                    : `${GLYPH.pencil} Edit ` +
                      (selCat === "fees"
                        ? "School Costs & Stationery"
                        : selCat === "uniforms"
                          ? "Uniforms"
                          : selCat === "shoes"
                            ? "Shoes"
                            : "Device")}
                </button>
              );
            })()}
            <HelpButton
              helpKey={
                selCat === "fees"
                  ? "responder_edit_fees"
                  : selCat === "uniforms"
                    ? "responder_edit_uniforms"
                    : selCat === "shoes"
                      ? "responder_edit_shoes"
                      : "responder_edit_device"
              }
              user={user}
            />
            {selCat === "laptop" && (
              <HelpButton helpKey="device_specs_responder" label="Device Specs" user={user} />
            )}
            {(function () {
              let catSt, catCol;
              if (selCat === "fees") {
                return null; // per-fee dropdowns are on each fee row below
              }
              catSt = selChild[selCat]?.approvalStatus || APPROVAL_STATUSES.PENDING;
              catCol = APPROVAL_COLOURS[catSt] || APPROVAL_COLOURS.Pending;
              // Per-item lock: a decided category locks ONLY after Confirm & Send
              // (req.approvalsConfirmedAt set). Pre-send everything stays editable so a
              // single item can be changed back to Pending/Needs Info without the global
              // "Revert to Pending". See docs/decision-approval-item-locking.md.
              const catLocked =
                requestLocked ||
                (!!req.approvalsConfirmedAt &&
                  (catSt === APPROVAL_STATUSES.APPROVED || catSt === APPROVAL_STATUSES.DECLINED));
              return (
                <div style={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
                  {!req.approvalsConfirmedAt &&
                    needsReason(catSt) &&
                    categoryReason(sel.ci, selCat) && (
                      <ReasonBubble
                        reason={categoryReason(sel.ci, selCat)}
                        onClick={function () {
                          openReasonModal(catSt, sel.ci, selCat, categoryReason(sel.ci, selCat));
                        }}
                      />
                    )}
                  <select
                    value={catSt}
                    onChange={function (ev) {
                      saveApproval(ev.target.value);
                    }}
                    disabled={saving || catLocked}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: `2px solid ${catCol.border}`,
                      fontSize: 15,
                      fontWeight: 700,
                      fontFamily: "inherit",
                      background: catCol.bg,
                      color: catCol.text,
                      width: 160,
                      cursor: catLocked ? "not-allowed" : "pointer",
                      opacity: catLocked ? 0.8 : 1,
                    }}
                  >
                    {APPROVAL_STATUS_VALUES.map(function (s) {
                      return (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      );
                    })}
                  </select>
                  {catLocked && (
                    <span style={{ fontSize: 11, color: "#607D79", marginLeft: 8 }}>Locked</span>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Edit mode — show only the relevant category editor inline */}
          {editing && sel && (
            <div
              style={{
                border: "2px solid #388E3C",
                borderRadius: 10,
                marginBottom: 16,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#388E3C",
                  color: "white",
                  padding: "10px 16px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 14 }}>
                  Editing:{" "}
                  {selCat === "fees"
                    ? "School Costs & Stationery"
                    : selCat === "uniforms"
                      ? "Uniforms"
                      : selCat === "shoes"
                        ? "Shoes"
                        : "Device"}{" "}
                  — Child {sel.ci + 1}
                </span>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={function () {
                      // Ephemeral staging (docs/ephemeral-staging-plan.md): the edit is held in the
                      // in-memory request and persisted ONLY at "Confirm & Send" — NO DB write here.
                      // The category onChange handlers below already fold each field into `req` via
                      // onSaveNoDb as it changes; this Save mirrors approve/decline (in-memory only)
                      // and consolidates all persistence onto the single Confirm & Send commit. A
                      // crash/refresh before confirm discards the edit and the request is unchanged.
                      const updatedChildren = (
                        latestChildrenRef.current ||
                        req.children ||
                        []
                      ).slice();
                      onSaveNoDb(
                        Object.assign({}, req, {
                          children: updatedChildren,
                          updatedBy: user.shelterName,
                          updatedAt: new Date().toISOString(),
                          approvalsConfirmedAt: null,
                        })
                      );
                      editSnapshotRef.current = null; // Save keeps the edits (Cancel restores the snapshot)
                      setEditing(false);
                      showSaved(`Saved ${GLYPH.check}`);
                    }}
                    style={{
                      background: "white",
                      color: "#388E3C",
                      border: "none",
                      borderRadius: 6,
                      padding: "6px 14px",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Save
                  </button>
                  <button
                    onClick={cancelEdit}
                    style={{
                      background: "rgba(255,255,255,0.2)",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.4)",
                      borderRadius: 6,
                      padding: "6px 14px",
                      fontSize: 13,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
              <div style={{ padding: 16, background: "#F1F8F2" }}>
                {selCat === "fees" && selChild && (
                  <ChildFeesSection
                    fees={selChild.fees || []}
                    onChange={function (newFees) {
                      // db-79: the fees form rebuilds each row and drops requestedAmount;
                      // re-attach the original (matched by feeType) so a changed amount can
                      // be flagged in the approval summary.
                      const reqFees = requestedByChildRef.current[sel.ci]?.fees || {};
                      // Stage 1 (save_request_children upsert prep): re-attach ONLY the fee's row
                      // id (the fees form drops it), matched by feeType — the editor is one row per
                      // type. That id is all the upsert needs from the client to UPDATE-by-id
                      // instead of delete+reinsert. Behaviour-neutral today: the RPC ignores ids.
                      // Server-owned columns (decision / SO / reason) are the upsert's job to
                      // preserve on the row, not the client's to resend.
                      const feeIdByType = {};
                      (selChild.fees || []).forEach(function (f) {
                        feeIdByType[f.feeType] = f.id;
                      });
                      const mergedFees = (newFees || []).map(function (nf) {
                        const withId =
                          feeIdByType[nf.feeType] != null
                            ? Object.assign({}, nf, { id: feeIdByType[nf.feeType] })
                            : nf;
                        return withId.requestedAmount == null && reqFees[nf.feeType] != null
                          ? Object.assign({}, withId, { requestedAmount: reqFees[nf.feeType] })
                          : withId;
                      });
                      const updated = Object.assign({}, selChild, { fees: mergedFees });
                      latestChildrenRef.current = (
                        latestChildrenRef.current ||
                        req.children ||
                        []
                      ).map(function (c, i) {
                        return i === sel.ci ? updated : c;
                      });
                      onSaveNoDb(Object.assign({}, req, { children: latestChildrenRef.current }));
                    }}
                    gender={selChild.gender}
                    yearLevel={selChild.yearLevel}
                    schoolName={selChild.schoolName}
                    user={user}
                    standardCosts={stdCosts}
                    refNo={req.refNo}
                    requestState={req.state}
                    hasLaptop={!!selChild.laptop}
                  />
                )}
                {selCat === "uniforms" && selChild && (
                  <ChildUniformsSection
                    uniforms={selChild.uniforms}
                    onChange={function (updated) {
                      // db-93: the uniforms form rebuilds the node and drops requestedAmount;
                      // re-attach the ORIGINAL requested total so the amount-change note is
                      // relative to what the DFV Service asked for (not the edited figure).
                      const reqUni = requestedByChildRef.current[sel.ci]?.uniforms;
                      const withReq =
                        updated && updated.requestedAmount == null && reqUni != null
                          ? Object.assign({}, updated, { requestedAmount: reqUni })
                          : updated;
                      const updChild = Object.assign({}, selChild, { uniforms: withReq });
                      latestChildrenRef.current = (
                        latestChildrenRef.current ||
                        req.children ||
                        []
                      ).map(function (c, i) {
                        return i === sel.ci ? updChild : c;
                      });
                      onSaveNoDb(Object.assign({}, req, { children: latestChildrenRef.current }));
                    }}
                    gender={selChild.gender}
                    yearLevel={selChild.yearLevel}
                    schoolName={selChild.schoolName}
                    shelterName={req.shelterName}
                    refNo={req.refNo}
                    user={user}
                  />
                )}
                {selCat === "shoes" && selChild && (
                  <ChildShoesSection
                    shoes={selChild.shoes}
                    onChange={function (updated) {
                      // db-79: re-attach the original requested shoe total if the form dropped it.
                      const reqShoes = requestedByChildRef.current[sel.ci]?.shoes;
                      const withReq =
                        updated && updated.requestedAmount == null && reqShoes != null
                          ? Object.assign({}, updated, { requestedAmount: reqShoes })
                          : updated;
                      const updChild = Object.assign({}, selChild, { shoes: withReq });
                      latestChildrenRef.current = (
                        latestChildrenRef.current ||
                        req.children ||
                        []
                      ).map(function (c, i) {
                        return i === sel.ci ? updChild : c;
                      });
                      onSaveNoDb(Object.assign({}, req, { children: latestChildrenRef.current }));
                    }}
                    yearLevel={selChild.yearLevel}
                    user={user}
                    standardCosts={null}
                  />
                )}
                {selCat === "laptop" && selChild && (
                  <ChildLaptopSection
                    laptop={selChild.laptop}
                    onChange={function (updated) {
                      // db-79: re-attach the original requested estimated cost if dropped.
                      const reqLaptop = requestedByChildRef.current[sel.ci]?.laptop;
                      const withReq =
                        updated && updated.requestedAmount == null && reqLaptop != null
                          ? Object.assign({}, updated, { requestedAmount: reqLaptop })
                          : updated;
                      const updChild = Object.assign({}, selChild, { laptop: withReq });
                      latestChildrenRef.current = (
                        latestChildrenRef.current ||
                        req.children ||
                        []
                      ).map(function (c, i) {
                        return i === sel.ci ? updChild : c;
                      });
                      onSaveNoDb(Object.assign({}, req, { children: latestChildrenRef.current }));
                    }}
                    yearLevel={selChild.yearLevel}
                    schoolName={selChild.schoolName}
                    user={user}
                    standardCosts={null}
                    deliveryAddresses={[]}
                    generalDelivery={req.deliveryAddress}
                  />
                )}
              </div>
            </div>
          )}

          {/* Read-only detail for selected category */}
          {!editing && selCat === "fees" && selChild.fees && selChild.fees.length > 0 && (
            <ChildItemReadView
              child={selChild}
              cat={selCat}
              internal
              repeats={repeats}
              repeatReview={repeatReview}
              repeatMonths={repeatMonths}
              feeHelpUser={user}
              renderFeeStatus={function (f, fi) {
                const fSt = f.approvalStatus || APPROVAL_STATUSES.PENDING;
                const fCol = APPROVAL_COLOURS[fSt] || APPROVAL_COLOURS.Pending;
                // Locks only after Confirm & Send (see catLocked above +
                // docs/decision-approval-item-locking.md).
                const feeLocked =
                  requestLocked ||
                  (!!req.approvalsConfirmedAt &&
                    (fSt === APPROVAL_STATUSES.APPROVED || fSt === APPROVAL_STATUSES.DECLINED));
                return (
                  <span style={{ display: "inline-flex", alignItems: "center" }}>
                    {!req.approvalsConfirmedAt && needsReason(fSt) && f.reason && (
                      <ReasonBubble
                        reason={String(f.reason)}
                        onClick={function () {
                          openFeeReasonModal(sel.ci, fi, fSt, String(f.reason));
                        }}
                      />
                    )}
                    <select
                      value={fSt}
                      onChange={function (ev) {
                        saveFeeApproval(sel.ci, fi, ev.target.value);
                      }}
                      disabled={saving || feeLocked}
                      style={{
                        padding: "6px 6px",
                        borderRadius: 6,
                        border: `2px solid ${fCol.border}`,
                        fontSize: 13,
                        fontWeight: 700,
                        fontFamily: "inherit",
                        background: fCol.bg,
                        color: fCol.text,
                        // 140 → 108 with padding trimmed 10px → 6px (John, 23 Aug
                        // 2026 — asked for −35%; that clips). "Needs Info" is the
                        // longest option: ~70px of 13px bold text + an ~18px dropdown
                        // arrow + 12px padding + 4px border ≈ 104px, so this leaves
                        // only a few px. Narrower needs a shorter DISPLAY label — the
                        // option text is the stored approval_status value, so that
                        // takes a display map, not an enum change.
                        width: 108,
                        cursor: feeLocked ? "not-allowed" : "pointer",
                        opacity: feeLocked ? 0.8 : 1,
                      }}
                    >
                      {APPROVAL_STATUS_VALUES.map(function (s) {
                        return (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        );
                      })}
                    </select>
                  </span>
                );
              }}
            />
          )}

          {!editing && selCat === "uniforms" && selChild.uniforms && (
            <ChildItemReadView
              child={selChild}
              cat={selCat}
              showBadge={false}
              uniformsActions={(function () {
                if (selChild.uniforms.approvalStatus !== APPROVAL_STATUSES.APPROVED) return null;
                const meta = supplierMeta(uniformSuppliers, selChild.uniforms.supplier);
                const isSL = meta.kind === "school_locker";
                // School Locker keeps its electronic order sheet AND (like Lowes) gets a
                // Print Authorisation. Standard authority suppliers get the authorisation only.
                if (!isSL && !meta.hasAuthorityDoc) return null;
                const btnStyle = {
                  background: "none",
                  border: `1px solid ${PANEL.heading}`,
                  color: PANEL.heading,
                  borderRadius: 6,
                  padding: "4px 12px",
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "inherit",
                };
                // "Print Order Sent" must only appear once the order has actually
                // gone — i.e. this session's submit succeeded, OR the request has
                // been confirmed (School Locker orders are submitted at Confirm &
                // Send now, not on approval), NOT the instant the uniform is approved.
                const slOrderSent =
                  slSubmitState === "success" || Boolean(req.approvalsConfirmedAt);
                return (
                  <React.Fragment>
                    {isSL && slOrderSent && (
                      <button
                        type="button"
                        onClick={async function () {
                          const args = {
                            child: Object.assign({}, selChild.uniforms, {
                              gender: selChild.gender,
                              yearLevel: selChild.yearLevel,
                              schoolName: selChild.schoolName,
                            }),
                            childIndex: sel.ci,
                            shelterName: req.shelterName,
                            refNo: req.refNo,
                            shelterCode: req.shelterCode || "",
                            shelterRef: req.shelterRef || "",
                            reqDate: req.date || "",
                            totalChildren:
                              (req.children || []).filter(function (ch) {
                                return (
                                  ch.uniforms &&
                                  ch.uniforms.approvalStatus === APPROVAL_STATUSES.APPROVED
                                );
                              }).length || 1,
                          };
                          // Order COPY as a jsPDF (matches the other uniform printouts).
                          // Fall back to the old HTML sheet only if jsPDF failed to load.
                          if (!(await openSchoolLockerOrderPdf(args))) {
                            printChild(Object.assign({ isSL: true }, args));
                          }
                        }}
                        style={btnStyle}
                      >
                        {`${GLYPH.printer} Print Order Sent`}
                      </button>
                    )}
                    {meta.hasAuthorityDoc && (
                      <button
                        type="button"
                        onClick={function () {
                          openUniformAuthorityPdf(req, selChild, sel.ci);
                        }}
                        style={btnStyle}
                      >
                        {`${GLYPH.printer} Print Authorisation`}
                      </button>
                    )}
                    {isSL && slSubmitState === "success" && (
                      <span
                        style={{
                          fontSize: 12,
                          color: "#2E7D32",
                          fontWeight: 700,
                          background: "#E8F5E9",
                          border: "1.5px solid #66BB6A",
                          borderRadius: 6,
                          padding: "4px 12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {`Sent to School Locker ${GLYPH.check} ${slSubmitMsg}`}
                      </span>
                    )}
                    {isSL && slSubmitState === "loading" && (
                      <span
                        style={{
                          fontSize: 12,
                          color: "#1565C0",
                          fontWeight: 700,
                          background: "#E3F2FD",
                          border: "1.5px solid #90CAF9",
                          borderRadius: 6,
                          padding: "4px 12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Submitting to School Locker...
                      </span>
                    )}
                    {isSL && slSubmitState === "error" && (
                      <span
                        style={{
                          fontSize: 12,
                          color: "#C62828",
                          fontWeight: 700,
                          background: "#FFEBEE",
                          border: "1.5px solid #EF9A9A",
                          borderRadius: 6,
                          padding: "4px 12px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {slSubmitMsg}
                      </span>
                    )}
                  </React.Fragment>
                );
              })()}
            />
          )}

          {!editing && selCat === "shoes" && selChild.shoes && (
            <ChildItemReadView child={selChild} cat={selCat} showBadge={false} internal />
          )}

          {!editing && selCat === "laptop" && selChild.laptop && (
            <ChildItemReadView
              child={selChild}
              cat={selCat}
              showBadge={false}
              internal
              repeats={repeats}
              repeatReview={repeatReview}
              repeatMonths={repeatMonths}
            />
          )}
        </div>
      )}
    </div>
  );
}
